import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { eatDayOf, formatIsoDay, validateEstimatedAge } from '@wodi/domain';
import type { PatientDetail } from '@wodi/shared';
import { Button, DateField, FieldGroup, Notice, RadioGroup, TextArea, TextField } from '@wodi/ui';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import { updatePatient } from '../api/patients.ts';
import { valueSetQuery } from '../api/registration-config.ts';
import styles from './patients.module.css';
import { REASON_MIN_LENGTH } from './reason-dialog.tsx';
import { ESTIMATED_AGE_MESSAGES } from './registration-model.ts';

export const VERSION_CONFLICT_MESSAGE =
  'Someone else changed this patient while you were editing. Reload to see their changes, then make yours again.';

interface Draft {
  given: string;
  middle: string;
  family: string;
  sexId: string;
  birthKind: 'exact' | 'estimated';
  birthDate: string | null;
  ageValue: string;
  ageUnit: 'years' | 'months';
  reason: string;
}

/**
 * "Correct details" (plan §6.5). Only changed fields are sent, with the version the
 * form started from; a name, sex or date-of-birth correction needs a reason, which is
 * kept in the patient's history.
 */
export function CorrectDetailsForm({
  patient,
  onDone,
}: {
  patient: PatientDetail;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const sexOptions = useQuery(valueSetQuery('patient_sex'));
  const concepts = (sexOptions.data ?? []).filter((c) => c.status === 'active');
  const currentSex = concepts.find(
    (c) => c.system === patient.sex.system && c.code === patient.sex.code,
  );
  const [draft, setDraft] = useState<Draft>({
    given: patient.name.given,
    middle: patient.name.middle ?? '',
    family: patient.name.family ?? '',
    sexId: currentSex?.id ?? '',
    birthKind: patient.birthDateEstimated ? 'estimated' : 'exact',
    birthDate: patient.birthDateEstimated ? null : patient.birthDate,
    ageValue:
      patient.birthDateEstimated && patient.age !== null && patient.age.unit !== 'days'
        ? String(patient.age.value)
        : '',
    ageUnit: patient.age?.unit === 'months' ? 'months' : 'years',
    reason: '',
  });
  const [tried, setTried] = useState(false);
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
  };

  const nameChanged =
    draft.given.trim() !== patient.name.given ||
    (draft.middle.trim() || null) !== patient.name.middle ||
    (draft.family.trim() || null) !== patient.name.family;
  const sexChanged = draft.sexId !== '' && draft.sexId !== currentSex?.id;
  const birthChanged =
    draft.birthKind === 'exact'
      ? patient.birthDateEstimated || draft.birthDate !== patient.birthDate
      : !patient.birthDateEstimated ||
        draft.ageValue !== (patient.age === null ? '' : String(patient.age.value));
  const anyChange = nameChanged || sexChanged || birthChanged;

  const errors: Record<string, string> = {};
  if (tried) {
    if (draft.given.trim() === '') errors['given'] = "Enter the patient's first name.";
    if (birthChanged && draft.birthKind === 'exact') {
      if (draft.birthDate === null) errors['birthDate'] = 'Enter the date of birth.';
      else if (draft.birthDate > formatIsoDay(eatDayOf(new Date()))) {
        errors['birthDate'] = "The date of birth can't be in the future.";
      }
    }
    if (birthChanged && draft.birthKind === 'estimated') {
      const value = Number(draft.ageValue);
      const checked = validateEstimatedAge({ unit: draft.ageUnit, value });
      if (draft.ageValue.trim() === '') errors['ageValue'] = 'Enter the estimated age.';
      else if (!checked.ok) errors['ageValue'] = ESTIMATED_AGE_MESSAGES[checked.code];
    }
    if (anyChange && draft.reason.trim().length < REASON_MIN_LENGTH) {
      errors['reason'] =
        `Say why the details are being corrected, in at least ${String(REASON_MIN_LENGTH)} characters.`;
    }
    if (!anyChange) errors['form'] = 'Nothing has changed yet.';
  }

  const save = useMutation({
    mutationFn: () => {
      const sex = concepts.find((c) => c.id === draft.sexId);
      return updatePatient(patient.id, {
        expectedVersion: patient.version,
        ...(nameChanged
          ? {
              name: {
                given: draft.given.trim(),
                middle: draft.middle.trim() || null,
                family: draft.family.trim() || null,
              },
            }
          : {}),
        ...(sexChanged && sex !== undefined ? { sex: { system: sex.system, code: sex.code } } : {}),
        ...(birthChanged
          ? {
              birth:
                draft.birthKind === 'exact'
                  ? { kind: 'exact' as const, birthDate: draft.birthDate ?? '' }
                  : {
                      kind: 'estimated' as const,
                      age: { unit: draft.ageUnit, value: Number(draft.ageValue) },
                    },
            }
          : {}),
        changeReason: draft.reason.trim(),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['patients', patient.id] });
      onDone();
    },
  });

  const error = save.error;
  const errorText =
    error === null
      ? null
      : error instanceof ApiError && error.code === 'version_conflict'
        ? VERSION_CONFLICT_MESSAGE
        : error instanceof ApiError
          ? error.message
          : "The correction wasn't saved. Try again.";

  return (
    <form
      className={styles.form}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        setTried(true);
        const ready =
          anyChange && draft.given.trim() !== '' && draft.reason.trim().length >= REASON_MIN_LENGTH;
        if (ready) save.mutate();
      }}
    >
      <FieldGroup legend="Correct details">
        <div className={styles.row}>
          <TextField
            label="First name"
            value={draft.given}
            onChange={(v) => {
              set('given', v);
            }}
            {...(errors['given'] === undefined ? {} : { errorMessage: errors['given'] })}
          />
          <TextField
            label="Middle name"
            value={draft.middle}
            onChange={(v) => {
              set('middle', v);
            }}
          />
          <TextField
            label="Family name"
            value={draft.family}
            onChange={(v) => {
              set('family', v);
            }}
          />
        </div>
        {concepts.length > 0 ? (
          <RadioGroup
            label="Sex"
            orientation="horizontal"
            value={draft.sexId}
            onChange={(v) => {
              set('sexId', v);
            }}
            options={concepts.map((c) => ({ id: c.id, label: c.display }))}
          />
        ) : null}
        <RadioGroup
          label="Date of birth"
          orientation="horizontal"
          value={draft.birthKind}
          onChange={(v) => {
            set('birthKind', v as Draft['birthKind']);
          }}
          options={[
            { id: 'exact', label: 'Date of birth known' },
            { id: 'estimated', label: 'Estimated age' },
          ]}
        />
        {draft.birthKind === 'exact' ? (
          <DateField
            label="Date of birth"
            value={draft.birthDate}
            onChange={(v) => {
              set('birthDate', v);
            }}
            maxValue={formatIsoDay(eatDayOf(new Date()))}
            {...(errors['birthDate'] === undefined ? {} : { errorMessage: errors['birthDate'] })}
          />
        ) : (
          <div className={styles.row}>
            <TextField
              label="Estimated age"
              inputMode="numeric"
              value={draft.ageValue}
              onChange={(v) => {
                set('ageValue', v);
              }}
              {...(errors['ageValue'] === undefined ? {} : { errorMessage: errors['ageValue'] })}
            />
            <RadioGroup
              label="Age in"
              orientation="horizontal"
              value={draft.ageUnit}
              onChange={(v) => {
                set('ageUnit', v as Draft['ageUnit']);
              }}
              options={[
                { id: 'years', label: 'Years' },
                { id: 'months', label: 'Months' },
              ]}
            />
          </div>
        )}
        <TextArea
          label="Reason for the correction"
          description="Needed for a name, sex or date of birth. Shown in the patient's history."
          value={draft.reason}
          onChange={(v) => {
            set('reason', v);
          }}
          {...(errors['reason'] === undefined ? {} : { errorMessage: errors['reason'] })}
        />
      </FieldGroup>
      {errors['form'] === undefined ? null : <Notice tone="info">{errors['form']}</Notice>}
      {errorText === null ? null : <Notice tone="error">{errorText}</Notice>}
      <div className={styles.actions}>
        <Button type="submit" variant="primary" isPending={save.isPending} pendingLabel="Saving…">
          Save correction
        </Button>
        <Button variant="quiet" onPress={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
